import { z } from 'zod'

// Código curto gerado pelo painel-zamply (src/lib/fast-login-redis.ts):
// 10 caracteres em [0-9a-z]. O link legado é base64(base64("email:token")),
// sempre bem maior que isso, então o tamanho basta para desambiguar.
const SHORT_FAST_LOGIN_CODE_REGEX = /^[a-z0-9]{10}$/

const FAST_LOGIN_REDIS_KEY_PREFIX = 'fast-login:'

export interface FastLoginCredentials {
  email: string
  token: string
}

/** Query param usado por `/l/[code]` para redirecionar erros ao login. */
export const FAST_LOGIN_QUERY_PARAM = 'fast_login'

/** Motivo enviado em `/conta/login?fast_login=<motivo>` pelo `/l/[code]`. */
export type FastLoginErrorReason = 'expired' | 'rate_limited' | 'unavailable'

const fastLoginPayloadSchema = z.object({
  email: z.string().min(1),
  token: z.string().min(1),
  unity_id: z.string().optional(),
  created_at: z.string().optional(),
})

export function isShortFastLoginCode(code: string): boolean {
  return SHORT_FAST_LOGIN_CODE_REGEX.test(code)
}

export function fastLoginRedisKey(code: string): string {
  return `${FAST_LOGIN_REDIS_KEY_PREFIX}${code}`
}

/**
 * Valida o JSON gravado pelo painel-zamply. Retorna `null` para qualquer
 * payload malformado — o chamador trata como link inválido, sem expor o erro.
 */
export function parseFastLoginPayload(
  raw: string | null
): FastLoginCredentials | null {
  if (!raw) {
    return null
  }

  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }

  const parsed = fastLoginPayloadSchema.safeParse(json)
  if (!parsed.success) {
    return null
  }

  return { email: parsed.data.email, token: parsed.data.token }
}

/**
 * Formato legado: base64(base64("{email}:{token}")). Mantido idêntico ao
 * comportamento anterior do `/l/[code]`.
 */
export function decodeLegacyFastLoginCode(code: string): FastLoginCredentials {
  const firstDecode = Buffer.from(code, 'base64').toString('utf-8')
  const decodedCode = Buffer.from(firstDecode, 'base64').toString('utf-8')

  const [email, token] = decodedCode.split(':')

  return { email, token }
}
